import { supabase } from "@/lib/supabase/client";
import type { HeroButtonStyle, HeadlineStyle } from "@/components/debug-controls-context";
import { getFixedHeroMediaFields } from "@/lib/hero-media";

export type HeroContent = {
  id: string;
  headline_line1: string;
  headline_line2: string;
  subline: string;
  cta_label: string;
  cta_target_type: "internal" | "external";
  cta_target_value: string;
  cta_style: HeroButtonStyle;
  headline_style: HeadlineStyle;
  background_type: "video" | "image";
  background_image_url: string | null;
  background_video_url: string | null;
  overlay_strength: number;
  /** Video-only CSS filter fields */
  video_brightness: number;
  video_saturation: number;
  video_blur: number;
  video_tint_color: string;
  video_tint_opacity: number;
  /** Vignette per edge (0 = off, 1 = full) */
  vignette_top: number;
  vignette_bottom: number;
  vignette_left: number;
  vignette_right: number;
  updated_at?: string;
};

export const HERO_CONTENT_ID = "00000000-0000-0000-0000-000000000001";

export const defaultHeroContent: HeroContent = {
  id: HERO_CONTENT_ID,
  headline_line1: "DEIN BESTES ICH",
  headline_line2: "STARTET HIER",
  subline: "Erlebe Fitness & Wellness auf über 3.000 m²\nim A3-Center Hösbach",
  cta_label: "Jetzt Termin sichern",
  cta_target_type: "internal",
  cta_target_value: "Probetraining",
  cta_style: "turquoise-pill",
  headline_style: "split-white-outline",
  ...getFixedHeroMediaFields(),
  overlay_strength: 0.3,
  video_brightness: 1.0,
  video_saturation: 1.0,
  video_blur: 0,
  video_tint_color: "#000000",
  video_tint_opacity: 0,
  vignette_top: 0,
  vignette_bottom: 0,
  vignette_left: 0,
  vignette_right: 0,
};

const normalizeHeroContent = (content?: Partial<HeroContent> | null): HeroContent => ({
  ...defaultHeroContent,
  ...(content ?? {}),
  ...getFixedHeroMediaFields(),
});

export async function fetchHeroContent(): Promise<HeroContent> {
  const { data, error } = await supabase
    .from("hero_content")
    .select("*")
    .eq("id", HERO_CONTENT_ID)
    .maybeSingle();

  if (error) {
    console.warn("Failed to load hero_content:", error.message);
    return defaultHeroContent;
  }

  if (!data) {
    return defaultHeroContent;
  }

  return normalizeHeroContent(data);
}

export async function saveHeroContent(content: HeroContent) {
  const payload = {
    ...normalizeHeroContent(content),
    updated_at: new Date().toISOString(),
  };

  const { error } = await supabase
    .from("hero_content")
    .upsert(payload, { onConflict: "id" });

  if (error) {
    throw error;
  }
}
